const STORAGE_KEY = 'apiToken'
const PRINCIPAL_KEY = 'apiPrincipal'
const CHANGE_EVENT = 'api-token-change'

const hasStorage = (): boolean => typeof window !== 'undefined' && Boolean(window.localStorage)

export const dispatchApiTokenChange = (): void => {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new Event(CHANGE_EVENT))
}

export const getStoredApiToken = (): string | null => {
  if (!hasStorage()) return null
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    return raw && raw.trim().length > 0 ? raw.trim() : null
  } catch {
    return null
  }
}

export const setStoredApiToken = (token: string): void => {
  if (!hasStorage()) return
  try {
    window.localStorage.setItem(STORAGE_KEY, token.trim())
  } catch {
    // ignore
  }
  dispatchApiTokenChange()
}

export type StoredPrincipal = {
  tenantKey: string
  role: 'viewer' | 'contributor' | 'editor' | 'admin'
  actorId: string | null
  tenantMemberId: string | null
  currentTokenId: string | null
  userId: string | null
  tenantMembershipId: string | null
  accountDisplayName: string | null
  email: string | null
  username: string | null
}

export const getStoredPrincipal = (): StoredPrincipal | null => {
  if (!hasStorage()) return null
  try {
    const raw = window.localStorage.getItem(PRINCIPAL_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as unknown
    if (!parsed || typeof parsed !== 'object') return null
    const value = parsed as Partial<StoredPrincipal>
    const tenantKey = typeof value.tenantKey === 'string' ? value.tenantKey : ''
    const role = value.role
    const actorId = value.actorId
    const tenantMemberId = value.tenantMemberId
    const currentTokenId = value.currentTokenId
    const userId = value.userId
    const tenantMembershipId = value.tenantMembershipId
    const accountDisplayName = value.accountDisplayName
    const email = value.email
    const username = value.username
    if (!tenantKey) return null
    if (role !== 'viewer' && role !== 'contributor' && role !== 'editor' && role !== 'admin') return null
    return {
      tenantKey,
      role,
      actorId: typeof actorId === 'string' ? actorId : null,
      tenantMemberId: typeof tenantMemberId === 'string' ? tenantMemberId : null,
      currentTokenId: typeof currentTokenId === 'string' ? currentTokenId : null,
      userId: typeof userId === 'string' ? userId : null,
      tenantMembershipId: typeof tenantMembershipId === 'string' ? tenantMembershipId : null,
      accountDisplayName: typeof accountDisplayName === 'string' ? accountDisplayName : null,
      email: typeof email === 'string' ? email : null,
      username: typeof username === 'string' ? username : null
    }
  } catch {
    return null
  }
}

export const setStoredPrincipal = (principal: StoredPrincipal): void => {
  if (!hasStorage()) return
  try {
    window.localStorage.setItem(PRINCIPAL_KEY, JSON.stringify(principal))
  } catch {
    // ignore
  }
  dispatchApiTokenChange()
}

export const clearStoredPrincipal = (): void => {
  if (!hasStorage()) return
  try {
    window.localStorage.removeItem(PRINCIPAL_KEY)
  } catch {
    // ignore
  }
  dispatchApiTokenChange()
}

export const clearStoredApiToken = (): void => {
  if (!hasStorage()) return
  try {
    window.localStorage.removeItem(STORAGE_KEY)
    window.localStorage.removeItem(PRINCIPAL_KEY)
  } catch {
    // ignore
  }
  dispatchApiTokenChange()
}

export const onApiTokenChange = (handler: () => void): (() => void) => {
  if (typeof window === 'undefined') return () => undefined
  const wrapped = () => handler()
  window.addEventListener('storage', wrapped)
  window.addEventListener(CHANGE_EVENT, wrapped as EventListener)
  return () => {
    window.removeEventListener('storage', wrapped)
    window.removeEventListener(CHANGE_EVENT, wrapped as EventListener)
  }
}
